/**
 * Intelligems A/B Test: Free Shipping Threshold (US ONLY)
 * -----------------------------------------------
 * Uses the Intelligems JS API to detect which test group the visitor belongs to.
 * Based on the group name (containing "$60" or "$75" etc.), it dynamically updates:
 *   1. Announcement bar text (replaces the entire h6 with "Free Shipping Over $X")
 *   2. Cart drawer (aside) shipping progress bar text & bar fill width
 *   3. Cart template page shipping progress bar text & bar fill width
 *
 * This experiment only runs for visitors in the US market.
 *
 * Uses MutationObserver and Shopify Cart API to dynamically re-apply changes
 * and recalculate remaining amounts after AJAX cart updates.
 *
 * Reference: https://docs.intelligems.io/developer-resources/javascript-api
 */
(function () {
  'use strict';

  // ── State ──────────────────────────────────────────────────────────────
  let activeThreshold = null;
  let activeGroupName = null;
  let initialized = false;
  let isUpdating = false;

  // ── Helpers ────────────────────────────────────────────────────────────

  /**
   * Determine the threshold from the group name.
   * Matches any group name containing a numeric threshold (e.g. "$60", "$75").
   */
  function resolveThreshold(groupName) {
    if (!groupName) return null;
    const name = groupName.toString();
    const match = name.match(/\d+/);
    if (match) {
      const threshold = parseInt(match[0], 10);
      // Ensure it's a reasonable threshold value to avoid false matches
      if (threshold >= 10 && threshold <= 250) {
        return threshold;
      }
    }
    return null;
  }

  /**
   * Format money in active currency with symbol.
   */
  function formatMoney(cents, currencyCode) {
    const amount = (cents / 100).toFixed(2);
    let symbol = '$'; // Default to US currency since this is US only
    if (currencyCode === 'USD') symbol = '$';
    else if (currencyCode === 'EUR') symbol = '€';
    else if (currencyCode === 'GBP') symbol = '£';
    return `${symbol}${amount}`;
  }

  /**
   * Update the announcement banner by replacing the entire content of h6 elements.
   */
  function updateAnnouncementBanner() {
    const announcementBanner = document.querySelector('.anouncement-banner');
    if (!announcementBanner || !activeThreshold) return;

    const h6Elements = announcementBanner.querySelectorAll('h6');
    const newText = `Free Shipping Over $${activeThreshold}`;

    h6Elements.forEach((h6) => {
      if (h6.textContent.trim() !== newText) {
        h6.textContent = newText;
      }
    });
  }

  /**
   * Fetch current cart data and dynamically update the progress bars.
   */
  function updateCartUI() {
    if (!activeThreshold) return;
    if (isUpdating) return;
    isUpdating = true;

    fetch('/cart.js')
      .then((res) => {
        if (!res.ok) throw new Error('Cart fetch failed');
        return res.json();
      })
      .then((cart) => {
        const cartTotal = cart.total_price;
        const cartCurrency = cart.currency || 'USD';
        const cartHasSubscription = cart.items.some((item) => item.selling_plan_allocation != null);
        const cartHasOnlyGiftCards = cart.items.length > 0 && cart.items.every((item) => item.gift_card === true);

        const thresholdCents = activeThreshold * 100;
        const hasFreeShipping = cartHasSubscription || cartHasOnlyGiftCards || (cartTotal >= thresholdCents);

        const remainingCents = thresholdCents - cartTotal;
        const formattedRemaining = formatMoney(remainingCents, cartCurrency);
        const progressPercent = Math.min(100, Math.max(0, (cartTotal / thresholdCents) * 100));

        // 1. Update Cart Aside (drawer)
        const cartAside = document.querySelector('aside.cart-aside');
        const cartAsideProgressBar = document.querySelector('.cart-aside__progress-bar');
        if (cartAsideProgressBar) {
          const textContainer = cartAsideProgressBar.querySelector('span.w-max');
          if (textContainer) {
            const newClass = hasFreeShipping ? 'w-max block font-grenette text-small' : 'w-max block';
            const newHTML = hasFreeShipping
              ? 'Enjoy FREE shipping!'
              : `Spend ${formattedRemaining} more for <span class="font-bold">free delivery</span>`;

            if (textContainer.className !== newClass) textContainer.className = newClass;
            if (textContainer.innerHTML !== newHTML) textContainer.innerHTML = newHTML;
          }

          const fillBar = cartAsideProgressBar.querySelector('.absolute.bg-red') || cartAsideProgressBar.querySelector('div[style*="width"]');
          if (fillBar) {
            const newWidth = `${progressPercent}%`;
            if (fillBar.style.width !== newWidth) fillBar.style.width = newWidth;
          }
        }

        // Update Cart Aside Footer Shipping & Total labels
        if (cartAside) {
          const shippingVal = cartAside.querySelector('.cart-aside__footer-shipping span:last-child');
          const totalVal = cartAside.querySelector('.cart-aside__footer-total span:last-child');

          if (shippingVal || totalVal) {
            const deliveryFeeAttr = cartAside.getAttribute('data-delivery-fee');
            // Default to 795 cents ($7.95) if not set, since this script only runs in US market
            const deliveryFeeCents = parseInt(deliveryFeeAttr || '795', 10);

            if (shippingVal) {
              const newShippingHTML = hasFreeShipping ? 'FREE' : formatMoney(deliveryFeeCents, cartCurrency);
              if (shippingVal.innerHTML !== newShippingHTML) {
                shippingVal.innerHTML = newShippingHTML;
              }
            }

            if (totalVal) {
              const finalTotalCents = hasFreeShipping ? cartTotal : (cartTotal + deliveryFeeCents);
              const newTotalHTML = formatMoney(finalTotalCents, cartCurrency);
              if (totalVal.innerHTML !== newTotalHTML) {
                totalVal.innerHTML = newTotalHTML;
              }
            }
          }
        }

        // 2. Update Cart Template page
        const cartFreeShippingIndicator = document.querySelector('.cart-free-shipping-indicator');
        if (cartFreeShippingIndicator) {
          const textContainer = cartFreeShippingIndicator.querySelector('.free-shipping-message');
          if (textContainer) {
            const newClass = hasFreeShipping ? 'w-max block font-grenette text-small free-shipping-message' : 'w-max block free-shipping-message';
            const newHTML = hasFreeShipping
              ? 'Enjoy FREE shipping!'
              : `Spend ${formattedRemaining} more for free delivery`;

            if (textContainer.className !== newClass) textContainer.className = newClass;
            if (textContainer.innerHTML !== newHTML) textContainer.innerHTML = newHTML;
          }

          const fillBar = cartFreeShippingIndicator.querySelector('.shipping-progress-bar');
          if (fillBar) {
            const newWidth = `${progressPercent}%`;
            if (fillBar.style.width !== newWidth) fillBar.style.width = newWidth;
          }
        }

        isUpdating = false;
      })
      .catch((err) => {
        console.error('[Intelligems] Error updating cart UI:', err);
        isUpdating = false;
      });
  }

  /**
   * Set up MutationObservers to re-apply experiment after AJAX cart re-renders.
   */
  function observeCartReRenders() {
    // Observe the cart aside for progress bar changes
    const cartAside = document.querySelector('aside.cart-aside');
    if (cartAside) {
      const asideObserver = new MutationObserver(() => {
        setTimeout(updateCartUI, 50);
      });
      asideObserver.observe(cartAside, { childList: true, subtree: true });
    }

    // Observe the cart template page for summary changes
    const cartTemplate = document.querySelector('section.cart-template');
    if (cartTemplate) {
      const templateObserver = new MutationObserver(() => {
        setTimeout(updateCartUI, 50);
      });
      templateObserver.observe(cartTemplate, { childList: true, subtree: true });
    }

    // Observe the announcement banner in case it updates dynamically
    const announcementBanner = document.querySelector('.anouncement-banner');
    if (announcementBanner) {
      const bannerObserver = new MutationObserver(() => {
        setTimeout(updateAnnouncementBanner, 50);
      });
      bannerObserver.observe(announcementBanner, { childList: true, subtree: true });
    }
  }

  // ── Initialization ─────────────────────────────────────────────────────

  function init() {
    if (initialized) return;

    // Check if US market
    const script = document.querySelector('script[src*="intelligems-shipping-experiment.js"]');
    const isUSMarket = script?.getAttribute('data-is-us') === 'true';
    if (!isUSMarket) {
      console.log('[Intelligems] Shipping experiment: visitor is not in the US market. Skipping.');
      return;
    }

    initialized = true;

    try {
      const experiments = window.igData?.user?.getExperiments() ?? [];

      if (!experiments.length) {
        console.log('[Intelligems] No active experiments found.');
        return;
      }

      console.log('[Intelligems] Active experiments:', experiments.length);

      // Check each experiment for a shipping threshold group
      for (const experiment of experiments) {
        const group = window.igData.user.getTestGroup(experiment.id);
        if (group?.name) {
          const threshold = resolveThreshold(group.name);
          if (threshold) {
            activeThreshold = threshold;
            activeGroupName = group.name;
            console.log('[Intelligems] Shipping experiment group:', group.name, '→ threshold:', activeThreshold);
            break;
          }
        }
      }

      if (!activeThreshold) {
        console.log('[Intelligems] No shipping threshold group matched.');
        return;
      }

      // Apply static texts immediately
      updateAnnouncementBanner();

      // Update dynamic elements and set up watchers
      updateCartUI();
      observeCartReRenders();

    } catch (err) {
      console.error('[Intelligems] Error initializing shipping experiment:', err);
    }
  }

  // ── Event Binding ──────────────────────────────────────────────────────
  window.addEventListener('ig:ready', () => init());

  // Fallback: if igData is already available (script loaded after Intelligems)
  if (window.igData?.user) {
    init();
  }
})();
